import './styles.css';
import { api, ApiError } from './api';
import { clear, h } from './dom';
import { experimentPage, loginPage, notFoundPage, sitePage, sitesPage } from './pages';
import { resultsPage, sharedPage } from './results-page';
import type { Ctx } from './pages';
import { interceptLinks, match, navigate, onRoute } from './router';

/** The app shell: header, sign-out, and whichever page the address bar asks for. */
class XpApp extends HTMLElement {
  private abort: AbortController | null = null;
  private stop: (() => void) | null = null;
  private seq = 0;

  connectedCallback(): void {
    interceptLinks(this);
    this.stop = onRoute(() => void this.render());
    void this.render();
  }

  disconnectedCallback(): void {
    this.stop?.();
    this.abort?.abort();
  }

  private async render(): Promise<void> {
    const mine = ++this.seq;
    this.abort?.abort();
    const abort = (this.abort = new AbortController());
    const route = match(location.pathname);
    clear(this);

    let email = '';
    if (route.name !== 'login' && route.name !== 'shared') {
      try {
        email = (await api<{ user: { email: string } }>('GET', '/me')).user.email;
      } catch (e) {
        if (mine !== this.seq) return;
        if (e instanceof ApiError && e.status === 401) return navigate('/login', true);
        this.append(
          h(
            'p',
            { role: 'alert', class: 'err' },
            'Could not reach the server. Reload to try again.',
          ),
        );
        return;
      }
      if (mine !== this.seq) return;
      const out = h('button', { type: 'button' }, 'Sign out');
      out.addEventListener('click', async () => {
        await api('POST', '/auth/logout', {}).catch(() => {});
        navigate('/login', true);
      });
      this.append(
        h(
          'header',
          {},
          h('a', { href: '/sites', class: 'brand' }, 'Experiments'),
          h('span', { class: 'spacer' }),
          h('span', { class: 'muted' }, email),
          out,
        ),
      );
    }

    const root = h('main', {});
    this.append(root);
    const ctx: Ctx = { root, signal: abort.signal, email };
    try {
      if (route.name === 'login') await loginPage(ctx);
      else if (route.name === 'sites') await sitesPage(ctx);
      else if (route.name === 'site') await sitePage(ctx, route.id);
      else if (route.name === 'experiment') await experimentPage(ctx, route.id);
      else if (route.name === 'results') await resultsPage(ctx, route.id);
      else if (route.name === 'shared') await sharedPage(ctx, route.token);
      else notFoundPage(ctx);
    } catch (e) {
      if (mine !== this.seq) return;
      if (e instanceof ApiError && e.status === 401) return navigate('/login', true);
      root.append(
        h(
          'p',
          { role: 'alert', class: 'err' },
          'Something went wrong loading this page. Reload to try again.',
        ),
      );
    }
  }
}

customElements.define('xp-app', XpApp);
